// src/components/my-journey/ShareableSeasonCard.jsx
// Feature 4 — Shareable Season Card
// Renders a styled card the manager can download as a PNG via html2canvas.
// The cardRef targets only the visual card div — the download button sits outside it.

import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Share2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { useLeagueData, useManagerContext } from '../../context/DataContext';

// ── Ordinal helper (same pattern as rest of codebase) ─────────────────────────
const getOrdinalSuffix = (n) => {
  if (!n || isNaN(n)) return '';
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

// ── Derive best GW from weeklyPoints (GW keys are integers 1–38, NOT strings) ─
const deriveBestGW = (weeklyPoints, managerName) => {
  const managerRow = weeklyPoints?.find((r) => r.Manager === managerName);
  if (!managerRow) return { gw: null, score: 0 };

  let bestGW = null;
  let bestScore = -Infinity;

  for (let gw = 1; gw <= 38; gw++) {
    const score = managerRow[gw];
    // GW keys in weeklyPoints are integer keys — access directly
    if (score !== undefined && score !== null && !isNaN(Number(score))) {
      const numScore = Number(score);
      if (numScore > bestScore) {
        bestScore = numScore;
        bestGW = gw;
      }
    }
  }

  return { gw: bestGW, score: bestScore > -Infinity ? bestScore : 0 };
};

const ShareableSeasonCard = () => {
  const { data } = useLeagueData();
  const { selectedManager, getManagerData } = useManagerContext();
  const cardRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  if (!selectedManager || !data) return null;

  const managerData = getManagerData();
  const totalPoints = managerData?.totalPoints || 0;
  const leaguePosition = managerData?.leaguePosition || '—';
  const pointsMissed = managerData?.pointsMissed || 0;

  // Primary archetype — sourced from data['Player Archetypes'] per data contract
  const primaryArchetype =
    data['Player Archetypes']?.find((r) => r.Manager === selectedManager)?.Primary_Archetype ||
    'FPL Enthusiast';

  // Best GW derived from integer-keyed weeklyPoints
  const { gw: bestGWNumber, score: bestGWScore } = deriveBestGW(
    data.weeklyPoints,
    selectedManager
  );

  // Numeric league position for ordinal suffix
  const positionNum = parseInt(leaguePosition, 10) || null;

  // ── Download handler ──────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsDownloading(true);
    setDownloadError(null);

    try {
      // Wait for all web fonts (Roboto) to be fully loaded so html2canvas
      // measures text with the correct font metrics before capture.
      await document.fonts.ready;

      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false,
        // Prevent layout shifts from scroll position or viewport size
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: document.documentElement.offsetWidth,
        windowHeight: document.documentElement.offsetHeight,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const anchor = document.createElement('a');
      anchor.href = dataUrl;
      anchor.download = `fpl-wrapped-${selectedManager.replace(/\s+/g, '-')}-2025-26.png`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (err) {
      console.error('Failed to generate card image:', err);
      setDownloadError('Could not generate image. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
    >
      {/* Section header */}
      <div
        className="px-5 py-4 flex items-center gap-3"
        style={{ borderBottom: '1px solid var(--c-border)', backgroundColor: 'var(--c-surface-1)' }}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: 'rgba(63,105,174,0.15)',
            border: '1px solid rgba(63,105,174,0.3)',
          }}
        >
          <Share2 size={18} style={{ color: 'var(--c-blue)' }} />
        </div>
        <div>
          <h3 className="font-bold text-sm text-white">Season Card</h3>
          <p className="text-xs" style={{ color: 'var(--c-grey)' }}>
            Download and share your season summary
          </p>
        </div>
      </div>

      <div className="p-5 flex flex-col items-center gap-4">
        {/* ── Exportable card ── */}
        {/* All colours hardcoded hex. No CSS vars, no Tailwind, no <p> tags (default margins),
            no CSS grid (gaps render white in dom-to-image), border:none on all containers. */}
        <div
          ref={cardRef}
          style={{
            width: '380px',
            background: 'linear-gradient(135deg, #3f69ae 0%, #44d5a3 100%)',
            borderRadius: '16px',
            padding: '2px',
            fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
            boxSizing: 'border-box',
            border: 'none',
            outline: 'none',
          }}
        >
          <div style={{ background: '#000006', borderRadius: '14px', border: 'none', outline: 'none', overflow: 'hidden' }}>

            {/* Top accent bar */}
            <div style={{ height: '4px', background: 'linear-gradient(90deg, #3f69ae 0%, #44d5a3 100%)', border: 'none' }} />

            {/* Body */}
            <div style={{ padding: '18px 22px 16px', border: 'none', backgroundColor: '#000006' }}>

              {/* Season label */}
              <div style={{ fontSize: '10px', lineHeight: '13px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#44d5a3', marginBottom: '8px', border: 'none' }}>
                FPL Wrapped · 2025/26
              </div>

              {/* Manager name */}
              <div style={{ fontSize: '24px', lineHeight: '28px', fontWeight: 900, color: '#ffffff', marginBottom: '3px', wordBreak: 'break-word', border: 'none' }}>
                {selectedManager}
              </div>

              {/* Archetype */}
              <div style={{ fontSize: '12px', lineHeight: '16px', color: '#8a8a8a', marginBottom: '18px', border: 'none' }}>
                {primaryArchetype}
              </div>

              {/* Stats — two rows of two, using flex to avoid grid gap artefacts */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0', border: 'none', backgroundColor: '#000006' }}>

                {/* Row 1 */}
                <div style={{ display: 'flex', gap: '0', marginBottom: '10px', border: 'none', backgroundColor: '#000006' }}>
                  {/* League position */}
                  <div style={{ flex: 1, marginRight: '5px', backgroundColor: '#1a2640', border: '1px solid #2d4a7a', borderRadius: '10px', padding: '5px 12px 8px', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: '9px', lineHeight: '12px', color: '#8a8a8a', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '2px' }}>
                      League Position
                    </div>
                    <div style={{ fontSize: '26px', lineHeight: '26px', fontWeight: 900, color: '#3f69ae' }}>
                      {positionNum || leaguePosition}
                      {positionNum && <span style={{ fontSize: '14px', lineHeight: '26px' }}>{getOrdinalSuffix(positionNum)}</span>}
                    </div>
                  </div>

                  {/* Total points */}
                  <div style={{ flex: 1, marginLeft: '5px', backgroundColor: '#162620', border: '1px solid #2a5045', borderRadius: '10px', padding: '5px 12px 8px', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: '9px', lineHeight: '12px', color: '#8a8a8a', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Total Points
                    </div>
                    <div style={{ fontSize: '26px', lineHeight: '26px', fontWeight: 900, color: '#44d5a3' }}>
                      {totalPoints.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Row 2 */}
                <div style={{ display: 'flex', gap: '0', border: 'none', backgroundColor: '#000006' }}>
                  {/* Best GW */}
                  <div style={{ flex: 1, marginRight: '5px', backgroundColor: '#1a1a1a', border: '1px solid #333333', borderRadius: '10px', padding: '5px 12px 8px', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: '9px', lineHeight: '12px', color: '#8a8a8a', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Best Gameweek
                    </div>
                    <div style={{ fontSize: '20px', lineHeight: '20px', fontWeight: 900, color: '#ffffff', marginBottom: '2px' }}>
                      {bestGWScore} pts
                    </div>
                    {bestGWNumber && (
                      <div style={{ fontSize: '10px', lineHeight: '13px', color: '#8a8a8a' }}>GW{bestGWNumber}</div>
                    )}
                  </div>

                  {/* Points missed */}
                  <div style={{ flex: 1, marginLeft: '5px', backgroundColor: '#1f1212', border: '1px solid #4a2020', borderRadius: '10px', padding: '5px 12px 8px', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: '9px', lineHeight: '12px', color: '#8a8a8a', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Points Missed
                    </div>
                    <div style={{ fontSize: '20px', lineHeight: '20px', fontWeight: 900, color: '#eb5757', marginBottom: '2px' }}>
                      {pointsMissed}
                    </div>
                    <div style={{ fontSize: '10px', lineHeight: '13px', color: '#8a8a8a' }}>left on the table</div>
                  </div>
                </div>

              </div>
            </div>

            {/* Separator — 1px div avoids Tailwind border-color bleed */}
            <div style={{ height: '1px', backgroundColor: '#1a1a22' }} />

            {/* Footer */}
            <div style={{ padding: '10px 22px', backgroundColor: '#0a0a0f', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '10px', lineHeight: '13px', color: '#44444f', fontWeight: 500 }}>FPL Wrapped 2025/26</div>
              <div style={{ fontSize: '10px', lineHeight: '13px', color: '#44d5a3', fontWeight: 700 }}>FPL Wrapped</div>
            </div>

          </div>
        </div>

        {/* Download button — outside cardRef so it does NOT appear in the PNG */}
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white"
          style={{
            background: isDownloading
              ? 'rgba(63,105,174,0.4)'
              : 'linear-gradient(135deg, #3f69ae 0%, #44d5a3 100%)',
            border: 'none',
            cursor: isDownloading ? 'wait' : 'pointer',
            opacity: isDownloading ? 0.7 : 1,
          }}
        >
          <Download size={15} />
          {isDownloading ? 'Generating…' : 'Download Card'}
        </motion.button>

        {/* Non-critical error feedback — does not expose internal details */}
        {downloadError && (
          <p className="text-xs text-center" style={{ color: 'var(--c-bad)' }}>
            {downloadError}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default ShareableSeasonCard;
